import { Badge, Modal, Popover, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { BiLogOutCircle } from "react-icons/bi";
import { FaRegUserCircle } from "react-icons/fa";
import { HiOutlineSpeakerphone } from "react-icons/hi";
import { ImNotification } from "react-icons/im";
import { IoChevronDown } from "react-icons/io5";
import { RiCloseLine } from "react-icons/ri";
import { VscBell } from "react-icons/vsc";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { university } from "../../globalVariables";
import { cloudFrontUrl, serverUrl } from "../../nestserver";
import { loginUser, selectUser } from "../../redux/slices/auth.slice";
import useIsGroupConsole from "../../apps/groupConsole/useIsGroupConsole";
import logo from "../../resources/feedlogo.png";
import {
  useAnnouncementNotifications,
  useNotifications,
  useUpdateManyNotification,
  useUpdateNotification,
  useUpdateNotificationCount,
} from "../../services/notification.service";

export default function Header() {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isGroupConsole = useIsGroupConsole();

  const notifications = useNotifications();
  const announcements = useAnnouncementNotifications();
  const updateNotificationMutation = useUpdateNotification();
  const updateManyNotificationMutation = useUpdateManyNotification();
  const updateNotificationCountMutation = useUpdateNotificationCount();

  const [showAllAnnouncements, setShowAllAnnouncements] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [hideShowNotification, setHideShowNotification] = useState(true);
  const [hideShowAnnouncement, setHideShowAnnouncement] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [linkedProfiles, setLinkedProfiles] = useState([]);
  const [groupSchools, setGroupSchools] = useState([]);

  const notifiactionShow = () => {
    setShowAllNotifications(true);
    setHideShowNotification(false);
  };
  const announcementShow = () => {
    setShowAllAnnouncements(true);
    setHideShowAnnouncement(false);
  };
  const cancelNotification = () => {
    setShowAllNotifications(false);
    setHideShowNotification(true);
  };
  const cancelAnnouncement = () => {
    setShowAllAnnouncements(false);
    setHideShowAnnouncement(true);
  };
  const getNotificationCount = async () => {
    const res = await axios.get(serverUrl + "/notification/count/mine", {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    setNotificationCount(res.data);
  };

  const getAnnouncementCount = async () => {
    const res = await axios.get(
      serverUrl + "/notification/mine/count/announcements",
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      },
    );
    setAnnouncementCount(res.data);
    return res.data;
  };

  const getLinkedProfiles = async () => {
    try {
      if (window.localStorage.getItem("jwt-token") && user) {
        const res = await axios.get(serverUrl + "/auth/linked-profiles", {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        if (res.data && res.data.result) {
          setLinkedProfiles(res.data.result);
        }
      }
    } catch (err) {
      console.log("Error loading linked profiles", err);
    }
  };

  const switchProfile = async (targetUserId) => {
    try {
      const hideLoading = message.loading("switching profile...", 0);
      const res = await axios.post(
        serverUrl + "/auth/switch-profile",
        { targetUserId },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      hideLoading();
      if (res.data && res.data.result) {
        const { access_token, user: newUser } = res.data.result;
        window.localStorage.setItem("jwt-token", access_token);
        dispatch(loginUser(newUser));
        message.success("Switched profile successfully!");
        navigate("/dashboard");
        window.location.reload();
      }
    } catch (err) {
      console.log("Error switching profile", err);
      message.error("Failed to switch profile");
    }
  };

  const getGroupSchools = async () => {
    try {
      if (window.localStorage.getItem("jwt-token") && user) {
        const res = await axios.get(serverUrl + "/auth/my-schools", {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        if (res.data && Array.isArray(res.data.result)) {
          setGroupSchools(res.data.result);
        }
      }
    } catch (err) {
      console.log("Error loading group schools", err);
    }
  };

  const selectSchool = async (schoolId) => {
    try {
      const hideLoading = message.loading("switching branch...", 0);
      const res = await axios.post(
        serverUrl + "/auth/select-school",
        { schoolId },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      hideLoading();
      if (res.data && res.data.result) {
        const { access_token, user: newUser } = res.data.result;
        window.localStorage.setItem("jwt-token", access_token);
        dispatch(loginUser(newUser));
        message.success("Switched branch successfully!");
        navigate("/dashboard/admin/home");
        window.location.reload();
      }
    } catch (err) {
      console.log("Error switching branch", err);
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Failed to switch branch",
      );
    }
  };

  useEffect(() => {
    getLinkedProfiles();
    getGroupSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      getAnnouncementCount();
      getNotificationCount();
    }, 10000);

    return () => clearInterval(intervalId);
  }, []);
  function dismissNotification(id) {
    updateNotificationMutation.mutate([id, { read: true }], {
      onSuccess: () => {
        message.success("Notification dismissed");
        queryClient.invalidateQueries("notifications");
        queryClient.invalidateQueries("announcement_notifications");
      },
    });
  }

  function dismissAllNotifications(type) {
    updateManyNotificationMutation.mutate(
      {
        notifications:
          type === "notifications"
            ? notifications?.data?.map((n) => n._id)
            : announcements?.data?.map((n) => n._id),
      },
      {
        onSuccess: () => {
          message.success("All notifications dismissed");
          queryClient.invalidateQueries("notifications");
          queryClient.invalidateQueries("announcement_notifications");
        },
      },
    );
  }
  function updateAllNotifications(type) {
    updateNotificationCountMutation.mutate(
      {
        notifications:
          type === "notifications"
            ? notifications?.data?.map((n) => n._id)
            : announcements?.data?.map((n) => n._id),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries("notifications");
          queryClient.invalidateQueries("announcement_notifications");
        },
      },
    );

    if (type === "notifications") {
      setNotificationCount(0);
    }
    if (type === "announcement") {
      setAnnouncementCount(0);
    }
  }

  function logout() {
    window.localStorage.removeItem("jwt-token");
    dispatch(loginUser(undefined));
    message.loading("logging off..", 1).then(() => {
      navigate("/auth/login");
      // history.replace("/auth/login");
      message.success("You have been logged off");
    });
  }

  const currentRoleKey = user?.role === "instructor" ? "teacher" : user?.role;
  const currentProfile = user?.[currentRoleKey];
  const activeSchoolName =
    groupSchools.find((s) => s.is_active)?.name || user?.school?.name;

  const dropdown = (
    <div className="w-72 -m-2 relative">
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white" />
      <div className="flex items-center gap-4 px-3 py-3 border-b border-gray-100">
        <img
          className="w-10 h-10 rounded-full ring-2 ring-primary-100 object-cover shrink-0"
          src={
            currentProfile?.profile_image
              ? cloudFrontUrl +
                "/" +
                user._id +
                "/" +
                currentProfile.profile_image
              : `https://ui-avatars.com/api/?name=${currentProfile?.first_name || "U"}+${currentProfile?.last_name || ""}&background=random`
          }
          alt="avatar"
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-800 truncate">
            {currentProfile?.first_name} {currentProfile?.last_name}
          </div>
          <div className="text-xs text-gray-400 capitalize truncate">
            {user?.role === "super_admin" ? "Super Admin" : user?.role}
          </div>
        </div>
      </div>

      <div className="p-2">
        <div
          className="flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer text-gray-700 hover:bg-gray-50 hover:text-primary-600 font-medium transition-colors"
          onClick={() => navigate(`/dashboard`)}
        >
          <FaRegUserCircle size={16} />
          <div className="text-sm">Profile</div>
        </div>
      </div>

      {groupSchools.length > 1 && (
        <div className="px-2 pb-2 pt-1 border-t border-gray-100">
          <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1.5 px-2 pt-1">
            Switch Branch
          </div>
          <div
            className={`group flex items-center gap-3 p-2 mb-1 rounded-lg border transition-all duration-200 ${
              isGroupConsole
                ? "bg-primary-50 border-primary-100 cursor-default"
                : "border-transparent hover:bg-gray-50 hover:border-gray-100 cursor-pointer"
            }`}
            onClick={() => !isGroupConsole && navigate("/dashboard/group")}
          >
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="text-xs font-semibold text-primary-600 truncate">
                All Branches
              </div>
              <div className="text-[10px] text-gray-400 truncate">
                Combined view, read-only
              </div>
            </div>
            {isGroupConsole ? (
              <div className="text-[9px] font-bold uppercase tracking-wide text-primary-500 shrink-0">
                Current
              </div>
            ) : (
              <IoChevronDown
                className="-rotate-90 text-gray-300 group-hover:text-primary-400 transition-colors shrink-0"
                size={12}
              />
            )}
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {groupSchools.map((school) => {
              const isCurrent = school.is_active && !isGroupConsole;
              return (
                <div
                  key={school._id}
                  className={`group flex items-center gap-3 p-2 rounded-lg border transition-all duration-200 ${
                    isCurrent
                      ? "bg-primary-50 border-primary-100 cursor-default"
                      : "border-transparent hover:bg-gray-50 hover:border-gray-100 cursor-pointer"
                  }`}
                  onClick={() => !isCurrent && selectSchool(school._id)}
                >
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-xs font-semibold text-gray-700 truncate">
                      {school.name}
                    </div>
                    <div className="text-[10px] text-gray-400 truncate">
                      {[school.refId, school.city].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {isCurrent ? (
                    <div className="text-[9px] font-bold uppercase tracking-wide text-primary-500 shrink-0">
                      Current
                    </div>
                  ) : (
                    <IoChevronDown
                      className="-rotate-90 text-gray-300 group-hover:text-primary-400 transition-colors shrink-0"
                      size={12}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {linkedProfiles.length > 0 && (
        <div className="px-2 pb-2 pt-1 border-t border-gray-100">
          <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1.5 px-2 pt-1">
            Switch Account
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {linkedProfiles.map((profile) => {
              const pRole = profile.role;
              const pProfile =
                profile[pRole === "instructor" ? "teacher" : pRole];
              const schoolName = profile.school?.name || "School";
              return (
                <div
                  key={profile._id}
                  className="group flex items-center gap-4 p-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-100 transition-all duration-200"
                  onClick={() => switchProfile(profile._id)}
                >
                  <img
                    className="w-8 h-8 rounded-full ring-1 ring-gray-100 group-hover:ring-primary-200 transition-all object-cover shrink-0"
                    src={
                      pProfile?.profile_image
                        ? cloudFrontUrl +
                          "/" +
                          profile._id +
                          "/" +
                          pProfile.profile_image
                        : `https://ui-avatars.com/api/?name=${pProfile?.first_name || "U"}+${pProfile?.last_name || "U"}&background=random`
                    }
                    alt="avatar"
                  />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-xs font-semibold text-gray-700 capitalize">
                      {pRole === "instructor" ? "Teacher" : pRole}
                    </div>
                    <div className="text-[10px] text-gray-400 truncate">
                      {schoolName}
                    </div>
                  </div>
                  <IoChevronDown
                    className="-rotate-90 text-gray-300 group-hover:text-primary-400 transition-colors shrink-0"
                    size={12}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="p-2 border-t border-gray-100">
        <div
          className="flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer text-red-500 hover:bg-red-50 hover:text-red-600 font-medium transition-colors"
          onClick={logout}
        >
          <BiLogOutCircle size={16} />
          <div className="text-sm">Logout</div>
        </div>
      </div>
    </div>
  );

  const notificationList = (
    <div className="divide-y w-80 ">
      {notifications?.data?.length ? (
        <>
          {notifications?.data?.map((notification) => (
            <div
              key={notification._id}
              className="flex items-center space-x-2 p-1 cursor-pointer hover hover:text-primary-500"
            >
              <ImNotification size={20} className="flex-none" />
              <div>
                <div>{notification?.title}</div>
                <div className="text-xs italic font-light">
                  {moment(notification?.createdAt).format("Do MMM YYYY")}
                </div>
                <div
                  className="text-xs line-clamp-1"
                  style={{ wordBreak: "break-word" }}
                >
                  {notification?.description}
                </div>
              </div>

              <RiCloseLine
                size={20}
                onClick={() => dismissNotification(notification._id)}
                className="text-red-500 hover:text-red-400 flex-none"
              />
            </div>
          ))}
        </>
      ) : (
        <div className="flex items-center space-x-2 py-1 cursor-pointer text-gray-500 italic">
          No new notifications
        </div>
      )}
    </div>
  );

  const announcementNotificationList = (
    <div className="divide-y w-80 ">
      {announcements?.data?.length ? (
        <>
          {announcements?.data?.slice(0, 7)?.map((announcement) => (
            <div
              key={announcement._id}
              className="flex items-center space-x-2 p-1 cursor-pointer hover hover:text-primary-500"
            >
              <ImNotification size={20} className="flex-none" />
              <div className="flex-grow">
                <div>{announcement?.title}</div>
                <div className="text-xs italic font-light">
                  {moment(announcement?.createdAt).format("Do MMM YYYY")}
                </div>
                <div
                  className="text-xs line-clamp-1"
                  style={{ wordBreak: "break-word" }}
                >
                  {announcement?.description}
                </div>
              </div>

              <RiCloseLine
                onClick={() => dismissNotification(announcement._id)}
                size={20}
                className="text-red-500 hover:text-red-400 flex-none"
              />
            </div>
          ))}
        </>
      ) : (
        <div className="flex items-center space-x-2 py-1 cursor-pointer text-gray-500 italic">
          No new announcements
        </div>
      )}
    </div>
  );

  function setUserImage(role) {
    return (
      <img
        className="w-8 h-8 rounded-full"
        src={
          user
            ? user[role === "instructor" ? "teacher" : role]?.profile_image
              ? cloudFrontUrl +
                "/" +
                user?._id +
                "/" +
                user[role === "instructor" ? "teacher" : role]?.profile_image
              : "https://ui-avatars.com/api/?name=" +
                user[role === "instructor" ? "teacher" : role]?.first_name +
                " " +
                user[role === "instructor" ? "teacher" : role]?.last_name
            : "https://ui-avatars.com/api/?name=TB"
        }
        alt="profile"
      />
    );
  }

  return (
    <div className="h-full bg-primary">
      <div className=" h-full flex flex-row items-center justify-between px-10 py-2">
        <div className="inline-flex flex-row items-center space-x-3 h-full">
          <div className=" inline-flex items-center bg-white rounded-md shadow px-2 py-1">
            <img className="w-10 h-10" src={logo} alt="logo" />
            <div className="text-primary-500 text-lg">{university}</div>
          </div>

          <div className="capitalize text-white font-normal text-lg">
            {user?.role === "super_admin" ? "Super Admin" : user?.role}{" "}
            Dashboard
          </div>

          {groupSchools.length > 1 && isGroupConsole && (
            <div className="inline-flex items-center gap-2 bg-white/15 rounded-md px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold mr-1" style={{ marginRight: "1rem" }}>
                Viewing
              </span>
              <span className="text-white text-sm font-medium truncate max-w-56">
                All Branches
              </span>
            </div>
          )}
          {groupSchools.length > 1 && !isGroupConsole && activeSchoolName && (
            <div className="inline-flex items-center gap-2 bg-white/15 rounded-md px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold mr-1" style={{ marginRight: "1rem" }}>
                Branch
              </span>
              <span className="text-white text-sm font-medium truncate max-w-56">
                {activeSchoolName}
              </span>
            </div>
          )}
        </div>

        {/* ------------------------------------------------------------- */}

        <div className="inline-flex flex-row items-center space-x-2 ml-auto">
          {hideShowNotification ? (
            <Popover
              placement="bottom"
              content={
                <>
                  {" "}
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {notificationList}
                  </div>
                  <div className="pt-3 flex space-x-4 items-center justify-center">
                    <div
                      onClick={notifiactionShow}
                      className="inline-block text-primary-500 opacity-100 hover:opacity-70 cursor-pointer"
                    >
                      View All
                    </div>
                    <div
                      onClick={() => dismissAllNotifications("notifications")}
                      className="inline-block text-red-500 opacity-100 hover:opacity-70 cursor-pointer"
                    >
                      Dismiss All
                    </div>
                  </div>
                </>
              }
              trigger="click"
              onVisibleChange={(visible) => {
                if (!visible && notificationCount) {
                  updateAllNotifications("notifications");
                }
              }}
            >
              <div className="w-10 h-10 rounded-full p-1 bg-primary-600 flex items-center justify-center opacity-75 hover:opacity-100 cursor-pointer">
                <Badge size="small" count={notificationCount}>
                  <VscBell className="text-white" size={25} />
                </Badge>
              </div>
            </Popover>
          ) : (
            <div className="w-10 h-10 rounded-full p-1 bg-primary-600 flex items-center justify-center opacity-75 hover:opacity-100 cursor-pointer">
              <Badge size="small" count={notificationCount}>
                <VscBell className="text-white" size={25} />
              </Badge>
            </div>
          )}

          {/*  notification list popup  */}
          <Modal
            title="All Notifications"
            visible={showAllNotifications}
            footer={null}
            onCancel={cancelNotification}
          >
            {notifications?.data?.map((notification) => (
              <div
                key={notification._id}
                className="flex items-center space-x-2 p-1 cursor-pointer hover hover:text-primary-500"
              >
                <ImNotification size={20} className="flex-none" />
                <div className="flex-grow">
                  <div>{notification?.title}</div>
                  <div
                    className="text-xs line-clamp-1"
                    style={{ wordBreak: "break-word" }}
                  >
                    {notification?.description}
                  </div>
                </div>

                <RiCloseLine
                  onClick={() => dismissNotification(notification._id)}
                  size={20}
                  className="text-red-500 hover:text-red-400 flex-none"
                />
              </div>
            ))}
          </Modal>
          {/*  notification list popup  */}

          {user?.role !== "admin" &&
            (hideShowAnnouncement ? (
              <Popover
                placement="bottom"
                content={
                  <>
                    {" "}
                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      {announcementNotificationList}
                    </div>
                    <div className="pt-3 flex space-x-4 items-center justify-center">
                      <div
                        onClick={announcementShow}
                        className="inline-block text-primary-500 opacity-100 hover:opacity-70 cursor-pointer"
                      >
                        View All
                      </div>
                      <div
                        onClick={() => dismissAllNotifications("announcement")}
                        className="inline-block text-red-500 opacity-100 hover:opacity-70 cursor-pointer"
                      >
                        Dismiss All
                      </div>
                    </div>
                  </>
                }
                trigger="click"
                onVisibleChange={(visible) => {
                  if (!visible && announcementCount) {
                    updateAllNotifications("announcement");
                  }
                }}
              >
                <div className="w-10 h-10 rounded-full p-1 bg-primary-600 flex items-center justify-center opacity-75 hover:opacity-100 cursor-pointer">
                  <Badge size="small" count={announcementCount}>
                    <HiOutlineSpeakerphone
                      className={`${"text-white"}`}
                      size={25}
                    />
                  </Badge>
                </div>
              </Popover>
            ) : (
              <div className="w-10 h-10 rounded-full p-1 bg-primary-600 flex items-center justify-center opacity-75 hover:opacity-100 cursor-pointer">
                <Badge size="small" count={announcementCount}>
                  <HiOutlineSpeakerphone
                    className={`${"text-white"}`}
                    size={25}
                  />
                </Badge>
              </div>
            ))}

          {/* announcement notification list popup  */}
          <Modal
            title="All Anouncements"
            visible={showAllAnnouncements}
            footer={null}
            onCancel={cancelAnnouncement}
          >
            {announcements?.data?.map((announcement) => (
              <div
                key={announcement._id}
                className="flex items-center space-x-2 p-1 cursor-pointer hover hover:text-primary-500"
              >
                <div className="flex-grow">
                  <div>{announcement?.title}</div>
                  {moment(announcement?.createdAt).format("Do MMM YYYY")}
                  <div
                    className="text-xs line-clamp-1"
                    style={{ wordBreak: "break-word" }}
                  >
                    {announcement?.description}
                  </div>
                </div>

                <RiCloseLine
                  onClick={() => dismissNotification(announcement._id)}
                  size={20}
                  className="text-red-500 hover:text-red-400 flex-none"
                />
              </div>
            ))}
          </Modal>
          {/* announcement notification list popup  */}

          <div>
            <Popover
              placement="bottomRight"
              content={dropdown}
              trigger="click"
              align={{ offset: [16, 4] }}
              arrow={false}
            >
              <div className="bg-white rounded-md text-black ">
                <div className="flex flex-row items-center space-x-2 cursor-pointer p-1 px-2">
                  <div>{setUserImage(user?.role)}</div>
                  <div className=" text-black">
                    {user &&
                      user[user?.role === "instructor" ? "teacher" : user?.role]
                        ?.first_name}
                  </div>
                  <div>
                    <IoChevronDown />
                  </div>
                </div>
              </div>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
}
